import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation } from 'wouter';
import GdprService from '../../../Services/GdprService';
import { URL_TRIPS_OVERVIEW, URL_TRIP_MAP } from '../../../router/constants';
import { IActions } from './Actions';
import LoaderService from '../../../Services/LoaderService';
import { localForageStore } from '../../../store/localForageStore';
import { IVehicles } from '../../Vehicles/types';
import {
  IFilterTripsOverview,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
} from '../interfaces';
import { TYPE_PRIVATE_ACTION, TYPE_PUBLIC_ACTION } from '../constants';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  filterForRequestAtom,
  isActiveAdvanceFilterAtom,
  isModalFormOpenedAtom,
  rideItemAtom,
  tripDataAtom,
} from '../TripsOverviewState';
import { changeRidePrivacy, convertTimeToSeconds, handleFilterKey } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IActionMenu extends IActions {
  rideActions: string[];
  parkingActions: string[];
}

function ActionsMenu(props: IActionMenu): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [, navigate] = useLocation();
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const [rideItem, setRideItem] = useRecoilState(
    rideItemAtom(props.data ? String(props.data.id) : '')
  );
  const [allowedActionsByGdpr, setAllowedActionsByGdpr] = React.useState<string[]>([]);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const setIsModalFormOpened = useSetRecoilState<string>(isModalFormOpenedAtom);
  const setTripData = useSetRecoilState<ITripsOverViewRecord | null>(tripDataAtom);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const getVehicleName = (id: number) => {
    const vehicle = Object.values(vehicles).find((i) => String(i.id) === String(id));
    if (vehicle && vehicle.name) {
      return vehicle.name;
    }
    return '';
  };

  const changeRideType = async (action: string) => {
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    LoaderService.showLoader(true);
    const defaultCheckedTableItems = {
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    };
    const res = await changeRidePrivacy(action, props.data, defaultCheckedTableItems, filter);
    if (res) {
      props.updateTable();
      // setRideItem((oldState: TripsOverViewState.ITripsOverViewRecord | null) => {
      //   if (oldState) {
      //     const newState = { ...oldState };
      //     newState.isPrivate = TYPE_PRIVATE_ACTION === action;
      //     return newState;
      //   }
      //   return null;
      // });
    }
    LoaderService.showLoader(false);
  };

  const redirectToTripMap = () => {
    if (props.data.customer.id && props.data.id) {
      localForageStore.setItem('rides', [`${props.data.customer.id}-${props.data.id}`]);
      localForageStore.setItem('multi-rides', null);
      window.open(`${URL_TRIP_MAP}`, '_blank');
    }
  };

  const handleModalForm = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    const type = event.currentTarget.getAttribute('data-type');
    if (type) {
      trackEvent(['ride-book', 'click', `Ride book single action: ${type}`]);
      const vehicleName = getVehicleName(props.data.vehicle.id);
      const data = { ...props.data, vehicleName };

      setIsModalFormOpened(type);
      setTripData(data);
      if (type.includes('new-ride')) {
        navigate(`${URL_TRIPS_OVERVIEW}/edit/${props.data.vehicle.id}/${props.data.id}`);
      }
    }
  };

  interface ShowInfoEvent extends React.MouseEvent<HTMLAnchorElement, MouseEvent> {
    currentTarget: HTMLAnchorElement;
  }

  const showInfo = (event: ShowInfoEvent): void => {
    event.preventDefault();
    if (rideItem) {
      trackEvent(['ride-book', 'click', 'Ride book single action: show detail']);
      setRideItem({ ...rideItem, open: !rideItem?.open });
    }
  };

  const handleAllowedType = React.useCallback(async () => {
    const allowedTypes = await GdprService.getRideGdprByTypes(
      ['basic', 'changeDriver', 'changeRidePrivacy', 'showRide', 'showRideDetail'],
      props.data.isPrivate,
      props.data.customer.id,
      props.data.driver.id
    );
    setAllowedActionsByGdpr(allowedTypes);
  }, [props.data.customer.id, props.data.driver.id, props.data.isPrivate]);

  React.useEffect(() => {
    handleAllowedType();
  }, [handleAllowedType]);

  return (
    <div
      className="ridebook-dropdown-menu dropdown-menu dropdown-menu-right dropdown-menu-custom fs-2"
      style={{ display: 'block', width: '200px' }}
      aria-labelledby="dropdown-actions-1"
    >
      {props.data && props.rideActions && props.rideActions.length > 0 && (
        <div>
          <div className="px-3 py-2 font-weight-bold border-bottom-on">
            {' '}
            {f({ id: 'table.rides' })}
          </div>
          {props.rideActions.includes('tag') && allowedActionsByGdpr.includes('basic') && (
            <a
              data-cy="ride-action-addUsage"
              href="#"
              title={f({ id: 'tripsOverview.modal.toAddUsage' })}
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type="usage"
              id={`usage-${props.data.id}`}
            >
              <i
                className="cmd-icon cmd-icon-single-folded-content-add mr-2 fs-4"
                style={{ marginLeft: '2px' }}
              />

              {f({ id: 'tripsOverview.modal.toAddUsage' })}
            </a>
          )}
          {props.rideActions.includes('comment') && allowedActionsByGdpr.includes('basic') && (
            <a
              data-cy="ride-action-addComment"
              href="#"
              title={f({ id: 'tripsOverview.modal.toAddComment' })}
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type="comment"
              id={`comment-${props.data.id}`}
            >
              <i className="cmd-icon cmd-icon-comment-add mr-2 fs-4" />
              {f({ id: 'tripsOverview.modal.toAddComment' })}
            </a>
          )}
          {props.rideActions.includes('driver') &&
            allowedActionsByGdpr.includes('changeDriver') && (
              <a
                data-cy="change-driver"
                href="#"
                title={f({ id: 'tripsOverview.modal.toChangeDriver' })}
                className="px-3 py-2 d-block text-decoration-none text-gray"
                onClick={handleModalForm}
                data-type="driver"
                id={`driver-${props.data.id}`}
              >
                <i
                  className="cmd-icon cmd-icon-single-user mr-2 fs-4"
                  style={{ marginLeft: '2px' }}
                />
                {f({ id: 'tripsOverview.modal.toChangeDriver' })}
              </a>
            )}
          {props.rideActions.includes('privacy') &&
            allowedActionsByGdpr.includes('changeRidePrivacy') && (
              <a
                data-cy={`ride-action-${
                  props.data.isPrivate !== true ? 'modal-privateRide' : 'modal-publicRide'
                }`}
                href="#"
                title={f({
                  id:
                    props.data.isPrivate !== true
                      ? 'tripsOverview.modal.privateRide'
                      : 'tripsOverview.modal.publicRide',
                })}
                className="px-3 py-2 d-block text-decoration-none text-gray"
                onClick={(e) => {
                  e.preventDefault();
                  changeRideType(
                    props.data.isPrivate === true ? TYPE_PUBLIC_ACTION : TYPE_PRIVATE_ACTION
                  );
                }}
              >
                {' '}
                {props.data.isPrivate !== true ? (
                  <>
                    <i className="cmd-icon cmd-icon-shifting-mode-private mr-2 fs-4" />

                    {f({ id: 'tripsOverview.modal.privateRide' })}
                  </>
                ) : (
                  <>
                    <i className="cmd-icon cmd-icon-ride-mode-work mr-2 fs-4" />
                    {f({ id: 'tripsOverview.modal.publicRide' })}
                  </>
                )}
              </a>
            )}
          {props.rideActions.includes('show') && allowedActionsByGdpr.includes('showRide') && (
            <a
              data-cy="ride-action-rideMap"
              href="/trip-map"
              onClick={redirectToTripMap}
              target="_blank"
              rel="noopener noreferrer"
              title={f({ id: 'tripsOverview.modal.rideMap' })}
              className="px-3 py-2 d-block text-decoration-none text-gray"
            >
              <i className="cmd-icon cmd-icon-play-simple mr-2 fs-4" />
              {f({ id: 'tripsOverview.modal.rideMap' })}
            </a>
          )}
          {props.rideActions.includes('detail') &&
            allowedActionsByGdpr.includes('showRideDetail') && (
              <a
                data-cy="ride-action-rides-rideDetail"
                href="#"
                title={f({ id: 'tables.tripsOverview.actions.ridePosition' })}
                className="px-3 py-2 d-block text-decoration-none text-gray"
                onClick={handleModalForm}
                data-type="detail"
              >
                <i className="cmd-icon cmd-icon-notes mr-2 fs-4" />
                {f({ id: 'tables.tripsOverview.actions.ridePosition' })}
              </a>
            )}
          {props.rideActions.includes('new-ride') && (
            <a
              data-cy="ride-action-rides-newRide-edit"
              href="#"
              title={f({ id: 'tripsOverview.modal.newRide.title.edit' })}
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type={`new-ride-${props.data.id}-${props.data.vehicle.id}`}
            >
              <i className="cmd-icon cmd-icon-detail mr-2 fs-4" />
              {f({ id: 'tripsOverview.modal.newRide.title.edit' })}
            </a>
          )}
          {props.rideActions.includes('remove-ride') && (
            <a
              data-cy="ride-action-rides-newRide-remove"
              href="#"
              title={f({ id: 'tripsOverview.modal.newRide.title.remove' })}
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type={`remove-ride-${props.data.id}-${props.data.vehicle.id}`}
            >
              <i className="cmd-icon cmd-icon-bin mr-2 fs-4" />
              {f({ id: 'tripsOverview.modal.newRide.title.remove' })}
            </a>
          )}
        </div>
      )}
      {props.data.parking && props.parkingActions && props.parkingActions.length > 0 && (
        <div>
          <div className="px-3 py-2 font-weight-bold border-bottom-on">
            {f({ id: 'table.parkings' })}
          </div>
          {props.parkingActions.includes('tag') && allowedActionsByGdpr.includes('basic') && (
            <a
              href="#"
              title={f({ id: 'tripsOverview.modal.toAddUsage' })}
              data-cy="parking-action-addUsage"
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type="usage-parking"
              id={`usage-parking-${props.data.id}`}
            >
              <i
                className="cmd-icon cmd-icon-single-folded-content-add mr-2 fs-4"
                style={{ marginLeft: '2px' }}
              />
              {f({ id: 'tripsOverview.modal.toAddUsage' })}
            </a>
          )}
          {props.parkingActions.includes('comment') && allowedActionsByGdpr.includes('basic') && (
            <a
              href="#"
              title={f({ id: 'tripsOverview.modal.toAddComment' })}
              data-cy="parking-action-addComment"
              className="px-3 py-2 d-block text-decoration-none text-gray"
              onClick={handleModalForm}
              data-type="comment-parking"
              id={`comment-parking-${props.data.id}`}
            >
              <i className="cmd-icon cmd-icon-comment-add mr-2 fs-4" />
              {f({ id: 'tripsOverview.modal.toAddComment' })}
            </a>
          )}
        </div>
      )}
      <div>
        <div className="px-3 py-2 font-weight-bold border-bottom-on">
          {f({ id: 'rides.tableOptions' })}
        </div>
        <a
          href="#"
          title={rideItem?.open ? f({ id: 'rides.hideDetail' }) : f({ id: 'rides.showDetail' })}
          data-cy="ride-action-showDetail"
          className="px-3 py-2 d-block text-decoration-none text-gray"
          onClick={showInfo}
          data-id={props.data.id}
        >
          <i
            className={`cmd-icon cmd-icon-${
              rideItem?.open ? 'colapse' : 'expand'
            }-row-action mr-2 fs-4`}
          />
          {rideItem?.open ? f({ id: 'rides.hideDetail' }) : f({ id: 'rides.showDetail' })}
        </a>
      </div>
    </div>
  );
}

export default ActionsMenu;
